import { Typography } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * A word the product uses that a new person has no reason to know, with the
 * explanation attached to the word.
 *
 * ## Why a glossary and not a sentence per screen
 *
 * "They won't remember our platform dictionary" — and the response to that
 * cannot be a paragraph of definitions at the top of every panel, because
 * then the panel is the dictionary and the thing somebody came for is below
 * it. It has to be the other way round: the screen reads as plain English,
 * and the two or three words that are genuinely ours carry their meaning
 * with them.
 *
 * **One table, so every screen says the same thing.** The failure this
 * prevents is the one the kit exists to prevent generally: six panels each
 * explaining `tag` slightly differently is six slightly different stories
 * about what a tag guarantees. A term defined here is defined once.
 *
 * **A dotted underline, because a tooltip nobody knows is there is a tooltip
 * nobody reads.** The marker is the affordance. It is also `cursor: help`
 * and reachable by keyboard, so the explanation is not mouse-only.
 *
 * Terms not in the table render as plain text rather than throwing — a
 * screen that crashed over a missing definition would be worse than one that
 * showed the word.
 */
const GLOSSARY = {
  tag: "A label on a record — `contract`, `payroll`, `employee`. Permissions are written against tags rather than individual records, so a person or an app is granted a kind of thing rather than a list of things.",
  tags: "Labels on a record — `contract`, `payroll`, `employee`. Permissions are written against tags rather than individual records, so a person or an app is granted a kind of thing rather than a list of things.",
  verb: "One thing an app is allowed to do, named as `thing.action` — `invoice.create`, `person.update`. An app may only do what it declared, and the kernel refuses anything else.",
  verbs: "The things an app is allowed to do, each named as `thing.action` — `invoice.create`, `person.update`. An app may only do what it declared, and the kernel refuses anything else.",
  unattended:
    "Nobody was signed in. The request came from a schedule, an alarm or a job rather than from a person clicking something — which matters, because the usual second limit (what the signed-in person may see) is not there.",
  attended:
    "A signed-in person was behind the request, so what the app reached was the narrower of what the app may touch and what that person may see.",
  manifest:
    "The reviewed file that says what one app may read and write. It is the app's ceiling: nothing the app does at runtime can widen it, and changing it is a commit somebody reviews.",
  ceiling:
    "The most this app may ever touch, set by its reviewed manifest. A person signed into it may reach less than this and never more.",
  effort:
    "How hard the model thinks before answering — low, medium or high. It is the setting that moves cost most for a given model, and higher is not automatically better.",
  cursor:
    "A bookmark in the source system. It records how far the last pull got, so the next one asks only for what changed instead of reading everything again.",
  connector:
    "Something that pulls records in from an outside system on a schedule — an accounting package, a case system — and writes them into this kernel.",
  agent:
    "An AI worker declared by an app. It is not a second identity: it acts under that app's ceiling, and naming it only adds a name to the record of what was done.",
  "at list":
    "Priced at Anthropic's published rates from the tokens each run reported. It is not an invoice — any discount or commitment on the account is not applied here.",
  token:
    "The unit models are billed in — roughly three quarters of a word. Both what is sent and what comes back are counted.",
  tokens:
    "The unit models are billed in — roughly three quarters of a word. Both what is sent and what comes back are counted.",
  "audit log":
    "The kernel's record of every write. It is written by the kernel rather than by the app, so an app cannot leave it out or write something else into it.",
  provenance:
    "Where a record came from and who put it there — which system, which app, which person, and whether anybody was signed in at the time.",
};

export default function Term({ name, children, ...rest }) {
  const key = String(name || children || "").toLowerCase();
  const meaning = GLOSSARY[key];
  if (!meaning) {
    return (
      <Typography component="span" variant="inherit" {...rest}>
        {children ?? name}
      </Typography>
    );
  }
  return (
    <Tooltip title={meaning}>
      <Typography
        component="span"
        variant="inherit"
        tabIndex={0}
        sx={{
          borderBottom: "1px dotted",
          borderColor: "text.disabled",
          cursor: "help",
        }}
        {...rest}
      >
        {children ?? name}
      </Typography>
    </Tooltip>
  );
}

export { GLOSSARY };
